import { useEffect, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';

const NAV_LINKS = [
  { to: '/', label: '首页' },
  { to: '/category/movie', label: '电影' },
  { to: '/category/tv', label: '电视剧' },
  { to: '/category/variety', label: '综艺' },
  { to: '/about', label: '关于我们' },
];

export default function Navbar() {
  const navigate = useNavigate();
  const location = useLocation();
  const [keyword, setKeyword] = useState('');
  const [menuOpen, setMenuOpen] = useState(false);
  const prevPath = useRef(location.pathname);

  useEffect(() => {
    if (prevPath.current !== location.pathname) {
      prevPath.current = location.pathname;
    }
    setMenuOpen(false);
  }, [location.pathname]);

  const submitSearch = (e: React.FormEvent) => {
    e.preventDefault();
    const q = keyword.trim();
    navigate(q ? `/search?q=${encodeURIComponent(q)}` : '/search');
    setMenuOpen(false);
  };

  const isActive = (to: string) =>
    to === '/' ? location.pathname === '/' : location.pathname.startsWith(to);

  return (
    <header className="sticky top-0 z-50 w-full border-b border-background-800/80 bg-background-950/85 backdrop-blur-md">
      <div className="mx-auto flex h-16 items-center gap-4 px-4 md:px-8">
        <Link to="/" className="flex items-center gap-2 whitespace-nowrap">
          <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-primary-500 text-background-50">
            <i className="ri-movie-2-line text-xl" />
          </span>
          <span className="font-heading text-xl font-bold tracking-wide text-foreground-50">
            桃子<span className="text-primary-500">视频</span>
          </span>
        </Link>

        <nav className="ml-4 hidden items-center gap-1 lg:flex">
          {NAV_LINKS.map((link) => (
            <Link
              key={link.to}
              to={link.to}
              className={`whitespace-nowrap rounded-md px-3 py-2 text-sm font-medium transition-colors ${
                isActive(link.to)
                  ? 'text-primary-400'
                  : 'text-foreground-300 hover:bg-background-800/70 hover:text-foreground-50'
              }`}
            >
              {link.label}
            </Link>
          ))}
        </nav>

        <form
          onSubmit={submitSearch}
          className="ml-auto hidden max-w-xs flex-1 items-center gap-2 rounded-full border border-background-700/80 bg-background-900 px-3 py-1.5 md:flex"
        >
          <i className="ri-search-line text-base text-foreground-400" />
          <input
            type="text"
            value={keyword}
            onChange={(e) => setKeyword(e.target.value)}
            placeholder="搜索电影、电视剧、综艺"
            className="w-full bg-transparent text-sm text-foreground-50 placeholder:text-foreground-500 focus:outline-none"
            aria-label="搜索视频"
          />
          <button
            type="submit"
            className="whitespace-nowrap rounded-full bg-primary-500 px-3 py-1 text-xs font-medium text-background-50 transition-colors hover:bg-primary-600"
          >
            搜索
          </button>
        </form>

        <button
          type="button"
          onClick={() => setMenuOpen((v) => !v)}
          className="ml-auto flex h-10 w-10 items-center justify-center rounded-md text-foreground-100 transition-colors hover:bg-background-800 lg:hidden"
          aria-label="打开菜单"
        >
          <i className={`${menuOpen ? 'ri-close-line' : 'ri-menu-line'} text-2xl`} />
        </button>
      </div>

      {menuOpen && (
        <div className="border-t border-background-800 bg-background-950 px-4 pb-5 pt-4 lg:hidden animate-fade-in">
          <form
            onSubmit={submitSearch}
            className="mb-4 flex items-center gap-2 rounded-full border border-background-700/80 bg-background-900 px-3 py-2"
          >
            <i className="ri-search-line text-base text-foreground-400" />
            <input
              type="text"
              value={keyword}
              onChange={(e) => setKeyword(e.target.value)}
              placeholder="搜索视频"
              className="w-full bg-transparent text-sm text-foreground-50 placeholder:text-foreground-500 focus:outline-none"
            />
          </form>
          <nav className="flex flex-col gap-1">
            {NAV_LINKS.map((link) => (
              <Link
                key={link.to}
                to={link.to}
                className={`rounded-md px-3 py-3 text-sm font-medium transition-colors ${
                  isActive(link.to)
                    ? 'bg-primary-500/15 text-primary-400'
                    : 'text-foreground-200 hover:bg-background-800'
                }`}
              >
                {link.label}
              </Link>
            ))}
          </nav>
        </div>
      )}
    </header>
  );
}